<div class="vstack gap-3">
    <ng-container *ngIf="!isSubmitted">
        <div class="text-center">
            <ng-container
                *ngTemplateOutlet="enquiryHeading; context: { title: 'Booking Enquiry' }"
            ></ng-container>
            <p class="text-secondary mb-0">
                For larger parties, please submit an enquiry and we'll be in touch to arrange your booking.
            </p>
        </div>

        <form
            class="vstack gap-3"
            [formGroup]="form"
            (ngSubmit)="onSubmit()"
        >
            <div class="bg-light rounded p-3">
                <div class="row text-center">
                    <div class="col-6">
                        <div class="text-secondary small">Date</div>
                        <div class="fw-semibold">{{ selectedDateAsDate | date: 'mediumDate' }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-secondary small">Time</div>
                        <div class="fw-semibold">{{ selectedTime | date: 'shortTime' }}</div>
                    </div>
                </div>
            </div>

            <div>
                <label class="form-label" for="enquiry-party-size">Party Size *</label>
                <input 
                    id="enquiry-party-size"
                    type="number" 
                    class="form-control"
                    formControlName="partySize"
                    min="1"
                    [class.is-invalid]="form.get('partySize')?.invalid && form.get('partySize')?.touched"
                >
                <div class="invalid-feedback" *ngIf="form.get('partySize')?.errors?.['required']">
                    Party size is required
                </div>
            </div>

            <div class="row g-3">
                <div class="col-12 col-sm-6">
                    <label class="form-label" for="enquiry-first-name">First Name *</label>
                    <input 
                        id="enquiry-first-name"
                        type="text" 
                        class="form-control" 
                        formControlName="firstName"
                        [class.is-invalid]="form.get('firstName')?.invalid && form.get('firstName')?.touched"
                    >
                    <div class="invalid-feedback" *ngIf="form.get('firstName')?.errors?.['required']">
                        First name is required
                    </div>
                </div>
                <div class="col-12 col-sm-6">
                    <label class="form-label" for="enquiry-last-name">Last Name *</label>
                    <input 
                        id="enquiry-last-name"
                        type="text" 
                        class="form-control" 
                        formControlName="lastName"
                        [class.is-invalid]="form.get('lastName')?.invalid && form.get('lastName')?.touched"
                    >
                    <div class="invalid-feedback" *ngIf="form.get('lastName')?.errors?.['required']">
                        Last name is required
                    </div>
                </div>
            </div>

            <div>
                <label class="form-label" for="enquiry-email-address">Email Address *</label>
                <input 
                    id="enquiry-email-address"
                    type="email" 
                    class="form-control" 
                    formControlName="emailAddress"
                    [class.is-invalid]="form.get('emailAddress')?.invalid && form.get('emailAddress')?.touched"
                >
                <div class="invalid-feedback" *ngIf="form.get('emailAddress')?.errors?.['required']">
                    Email address is required
                </div>
                <div class="invalid-feedback" *ngIf="form.get('emailAddress')?.errors?.['email']">
                    Please enter a valid email address
                </div>
            </div>

            <div>
                <label class="form-label" for="enquiry-phone-number">Phone Number *</label>
                <ngx-intl-tel-input
                    cssClass="form-control"
                    [preferredCountries]="[phoneNumberCountryCode]"
                    [enableAutoCountrySelect]="true"
                    [enablePlaceholder]="true"
                    [searchCountryFlag]="true"
                    [searchCountryField]="[SearchCountryField.Iso2, SearchCountryField.Name]"
                    [selectFirstCountry]="false"
                    [selectedCountryISO]="phoneNumberCountryCode"
                    [separateDialCode]="false"
                    [maxLength]="15"
                    [numberFormat]="PhoneNumberFormat.International"
                    [phoneValidation]="true"
                    inputId="enquiry-phone-number"
                    name="phone"
                    formControlName="phoneNumber"
                >
                </ngx-intl-tel-input>
                <div class="invalid-feedback d-block" *ngIf="form.get('phoneNumber')?.invalid && form.get('phoneNumber')?.touched">
                    Please enter a valid phone number
                </div>
            </div>

            <div>
                <label class="form-label" for="enquiry-message">Message (optional)</label>
                <textarea 
                    id="enquiry-message"
                    class="form-control" 
                    formControlName="message"
                    rows="3"
                    placeholder="Tell us about your booking requirements, special requests, or any other details..."
                ></textarea>
            </div>

            <div class="alert alert-danger" *ngIf="errorMessage">
                {{ errorMessage }}
            </div>

            <button 
                type="submit"
                class="btn btn-primary w-100"
                [disabled]="isSubmitting"
                [attr.aria-busy]="isSubmitting"
            >
                <span
                    *ngIf="isSubmitting"
                    class="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                ></span>
                Submit Enquiry
            </button>
        </form>
    </ng-container>

    <ng-container *ngIf="isSubmitted">
        <div class="text-center py-4">
            <div class="mb-3">
                <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
            </div>
            <ng-container
                *ngTemplateOutlet="enquiryHeading; context: { title: 'Enquiry Submitted' }"
            ></ng-container>
            <p class="text-secondary mb-3">
                Thank you for your enquiry. The {{ venue.displayName }} team will be in touch shortly to discuss your booking.
            </p>
            <p class="text-secondary small">
                Please check your email for confirmation.
            </p>
        </div>
    </ng-container>
</div>

<ng-template #enquiryHeading let-title="title">
    <div
        class="enquiry-form-heading mb-2"
        [class.enquiry-form-heading-with-back]="showBackButton"
    >
        @if (showBackButton) {
            <button
                type="button"
                class="enquiry-form-back btn btn-outline-secondary d-inline-flex align-items-center gap-1 w-auto"
                data-testid="back-booking-step"
                aria-label="Back to previous booking step"
                (click)="backRequested.emit()"
            >
                <img
                    src="assets/left/back.svg"
                    width="20"
                    height="20"
                    alt=""
                    aria-hidden="true"
                />
                <span>Back</span>
            </button>
        }
        <h5 class="fw-semibold mb-0">{{ title }}</h5>
        @if (showBackButton) {
            <span aria-hidden="true"></span>
        }
    </div>
</ng-template>
